import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchSessionLogs, type LogEvent } from '../../lib/api/sessions';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTs(ts: number): string {
  return new Date(ts).toISOString().replace('T', ' ').replace('Z', '');
}

// ---------------------------------------------------------------------------
// Log line renderer — colour-codes common log levels
// ---------------------------------------------------------------------------

const LOG_PATTERNS: [RegExp, string][] = [
  [/\berror\b|\bERROR\b|\bException\b|\bfatal\b|\bFATAL\b/, 'text-red-400'],
  [/\bwarn\b|\bWARN\b|\bwarning\b/,                         'text-yellow-400'],
  [/\binfo\b|\bINFO\b/,                                      'text-green-400'],
  [/\bdebug\b|\bDEBUG\b/,                                    'text-slate-400'],
];

const LogLine: React.FC<{ event: LogEvent }> = ({ event }) => {
  const colorClass = LOG_PATTERNS.find(([re]) => re.test(event.message))?.[1] ?? 'text-slate-200';
  const ts = formatTs(event.timestamp);

  return (
    <div className="flex gap-3 font-mono text-xs leading-relaxed hover:bg-white/5 px-1 rounded">
      <span className="shrink-0 text-slate-500 select-none">{ts}</span>
      <span className={`break-all ${colorClass}`}>{event.message}</span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main viewer — the log-fetching + rendering core shared by the full
// `/session-logs` page and the epic drawer's "Raw logs" tab (UX-04).
// ---------------------------------------------------------------------------

export interface SessionLogViewerProps {
  /** Reduced ECS task ID — never a full ARN (see lib/sessions/links.ts). */
  taskId: string;
  startedAt?: string;
  finishedAt?: string;
  /** Extra content rendered at the end of the toolbar (e.g. an "Open full page" link). */
  toolbarExtra?: React.ReactNode;
}

export const SessionLogViewer: React.FC<SessionLogViewerProps> = ({
  taskId,
  startedAt,
  finishedAt,
  toolbarExtra,
}) => {
  const [filter, setFilter]         = useState('');
  const [autoScroll, setAutoScroll] = useState(true);
  const [copied, setCopied]         = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const isRunning = !finishedAt;

  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: ['session-logs', taskId, startedAt, finishedAt],
    queryFn: () =>
      taskId
        ? fetchSessionLogs({ task: taskId, startTime: startedAt, endTime: finishedAt })
        : Promise.resolve({ events: [], nextToken: null, logGroup: '', taskId: '', warning: 'No task provided' }),
    enabled: true,
    staleTime: isRunning ? 0 : Infinity,
    refetchInterval: isRunning ? 5_000 : false,
  });

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    const el = bottomRef.current;
    if (autoScroll && el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }, [dataUpdatedAt, autoScroll]);

  const handleCopy = useCallback(() => {
    if (!data?.events) return;
    const text = data.events
      .map((e) => `${formatTs(e.timestamp)}  ${e.message}`)
      .join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [data]);

  const filteredEvents = (data?.events ?? []).filter(
    (e) => !filter || e.message.toLowerCase().includes(filter.toLowerCase()),
  );

  return (
    <div className="flex flex-col h-full min-h-0">
      {data?.logGroup && (
        <div className="text-[10px] text-slate-600 font-mono truncate px-4 pt-2 bg-slate-800 border-b border-slate-700">
          {data.logGroup} › {data.streamPrefix ?? data.taskId}
        </div>
      )}

      {/* ── Toolbar ─────────────────────────────────────────────── */}
      <div className="bg-slate-800 border-b border-slate-700 px-4 py-2 flex items-center gap-3">
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter logs…"
          className="flex-1 max-w-xs bg-slate-700 border border-slate-600 rounded px-2 py-1 text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-blue-500"
        />
        <span className="text-xs text-slate-500">
          {filteredEvents.length} line{filteredEvents.length !== 1 ? 's' : ''}
          {filter ? ' (filtered)' : ''}
        </span>
        <label className="flex items-center gap-1 text-xs text-slate-400 cursor-pointer">
          <input
            type="checkbox"
            checked={autoScroll}
            onChange={(e) => setAutoScroll(e.target.checked)}
            className="accent-blue-500"
          />
          Auto-scroll
        </label>
        <button
          type="button"
          onClick={handleCopy}
          disabled={!data?.events?.length}
          className="text-xs border border-slate-600 rounded px-2 py-1 text-slate-300 hover:bg-slate-700 disabled:opacity-40"
        >
          {copied ? '✓ Copied' : 'Copy all'}
        </button>
        {toolbarExtra}
      </div>

      {/* ── Log pane ─────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-0.5 bg-slate-900">
        {isLoading && (
          <div className="text-slate-500 text-xs animate-pulse">Loading logs…</div>
        )}

        {error && (
          <div className="text-red-400 text-xs bg-red-900/20 border border-red-700 rounded p-2">
            {(error as Error).message}
          </div>
        )}

        {data?.warning && (
          <div className="text-yellow-400 text-xs bg-yellow-900/20 border border-yellow-700 rounded p-2 mb-3">
            ⚠ {data.warning}
          </div>
        )}

        {!isLoading && !error && filteredEvents.length === 0 && (
          <div className="text-slate-600 text-xs italic">
            {filter ? 'No lines match the filter.' : 'No log events found for this time range.'}
          </div>
        )}

        {filteredEvents.map((e, i) => (
          <LogLine key={i} event={e} />
        ))}

        <div ref={bottomRef} />
      </div>

      {/* ── Footer ───────────────────────────────────────────────── */}
      {data?.nextToken && (
        <div className="bg-slate-800 border-t border-slate-700 px-4 py-2 text-xs text-slate-500 text-center">
          More logs available — use the API&apos;s <code className="font-mono">nextToken</code> to paginate.
        </div>
      )}
    </div>
  );
};

export default SessionLogViewer;
